<script setup>

import { ref, computed, onMounted } from 'vue';
import { required, email, helpers } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import Lottie from "@/components/widgets/lottie.vue";
import animationData from "@/components/widgets/rhvddzym.json";
import store from '../../state/store';

const state = ref({
  email: ''
});

const rules = {
   email: {
      required: helpers.withMessage("Email is required", required),
      email: helpers.withMessage("Please enter valid email", email),
    }
}

const loading = computed(() => store.state.globals.loading);
const notification = computed(() => store.state.notification);
const success = computed(() => store.state.auth.success);
const submitted = ref(false);

const defaultOptions = ref({
  animationData
});

const v$ = useVuelidate(rules, state);

const resetPassword = async () => {
  submitted.value = true;
  v$.value.$touch();

  if (v$.value.$invalid) return;

  try {
    await store.dispatch('auth/resetPassword', state.value);
  } catch (err) {
    console.log(err);
  }
}

onMounted(() => {
  store.commit('auth/success', false);
  store.dispatch('notification/clear', null, { root: true});
})
</script>

<template>
  <div class="auth-page-wrapper pt-5">
    <!-- auth page bg -->
    <div class="auth-one-bg-position auth-one-bg" id="auth-particles">
      <div class="bg-overlay"></div>

      <div class="shape">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          viewBox="0 0 1440 120"
        >
          <path
            d="M 0,36 C 144,53.6 432,123.2 720,124 C 1008,124.8 1296,56.8 1440,40L1440 140L0 140z"
          ></path>
        </svg>
      </div>
    </div>

    <!-- auth page content -->
    <div class="auth-page-content">
      <div class="container">
        <div class="row">
          <div class="col-lg-12">
            <div class="text-center mt-sm-5 mb-4 text-white-50">
              <div>
                <router-link to="/" class="d-inline-block auth-logo">
                  <img
                    src="@/assets/images/qpax/logo.png"
                    alt=""
                    height="150"
                  />
                </router-link>
              </div>
              <p class="mt-3 fs-15 fw-medium">
                Traffic Citation Management System
              </p>
            </div>
          </div>
        </div>
        <!-- end row -->

        <div class="row justify-content-center">
          <div class="col-md-8 col-lg-6 col-xl-5">
            <b-overlay :show="loading">
              <div class="card mt-4">
                <div v-if="!success" class="card-body p-4">
                  <div class="text-center mt-2">
                    <h5 class="text-primary">Forgot Password?</h5>
                    <p class="text-muted">Reset password with TCMS</p>

                    <Lottie
                      class="avatar-xl"
                        colors="primary:#45CB85,secondary:#4b38b3"
                        :options="defaultOptions"
                        :height="120"
                        :width="120"
                      />
                    
                  </div>

                  <b-alert v-if="notification && notification.message" 
                    show :variant="notification.type" class="fade show" role="alert">
                      {{ notification.message}}
                  </b-alert>

                  <div class="p-2">
                    <form @submit.prevent="resetPassword">
                      <div class="mb-4">
                        <label class="form-label">Email</label>
                        <input
                          autofocus
                          type="email"
                          v-model="state.email"
                          class="form-control"
                          id="email"
                          :class="{ 'is-invalid': submitted && v$.email.$error }"
                          placeholder="Enter Email"
                        />
                        <div
                          v-for="(item, index) in v$.email.$errors"
                          :key="index"
                          class="invalid-feedback"
                        >
                          <span v-if="item.$message">{{ item.$message }}</span>
                        </div>
                      </div>

                      <div class="text-center mt-4">
                        <button class="btn btn-success w-100" type="submit">
                          Send Reset Link
                        </button>
                      </div>
                    </form>
                    <!-- end form -->
                  </div>
                </div>
                <div v-if="success" class="card-body p-4">
                  <div class="text-center mt-2">
                    <Lottie
                      class="avatar-xl"
                        colors="primary:#45CB85,secondary:#4b38b3"
                        :options="defaultOptions"
                        :height="120"
                        :width="120"
                      />
                  </div>

                  <div class="mt-4 text-center">
                    <p class="mb-0">
                      Email sent successfully. Check your mail inbox
                    </p>
                    <router-link
                      to="/login"
                      class="fw-semibold text-primary text-decoration-underline"
                    >
                      Click here to Login
                    </router-link>
                  </div>
                </div>
                <!-- end card body -->
              </div>
            </b-overlay>
            <!-- end card -->

            <div v-if="!success" class="mt-4 text-center">
              <p class="mb-0">
                Wait, I remember my password...
                <router-link
                  to="/login"
                  class="fw-semibold text-primary text-decoration-underline"
                >
                  Click here
                </router-link>
              </p>
            </div>
          </div>
        </div>
        <!-- end row -->
      </div>
      <!-- end container -->
    </div>
    <!-- end auth page content -->

    <!-- footer -->
    <footer class="footer">
      <div class="container">
        <div class="row">
          <div class="col-lg-12">
            <div class="text-center">
              <p class="mb-0 text-muted">
                &copy; {{ new Date().getFullYear() }} QPAX Traffic Systems, Inc.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
    <!-- end Footer -->
  </div>
  <!-- end auth-page-wrapper -->
</template>
